import api from './api';
import type { User, Subscription, SubscriptionTier } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface UpdateProfileRequest {
  name?: string;
  birthYear?: number | null;
  stateOfResidence?: string | null;
}

export interface TenantProfile {
  id: string;
  name: string;
  birthYear: number | null;
  stateOfResidence: string | null;
  hasBirthYear: boolean;
  profileUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface MfaSetupResponse {
  secret: string;
  provisioningUri: string;
  qrCodeUrl: string;
}

export interface MfaVerifyRequest {
  code: string;
}

export interface MfaVerifyResponse {
  mfaEnabled: boolean;
  recoveryCodes: string[];
  message?: string;
}

export interface MfaDisableRequest {
  password: string;
}

// ============================================================================
// SMS MFA Types
// ============================================================================

export interface SmsMfaSetupRequest {
  phoneNumber: string;
  consentGiven: boolean;
}

export interface SmsMfaSetupResponse {
  phoneLastFour: string;
  expiresInMinutes: number;
  message: string;
}

export interface SmsMfaVerifyRequest {
  code: string;
}

export interface SmsMfaVerifyResponse {
  smsEnabled: boolean;
  phoneVerified: boolean;
  message: string;
}

export interface SmsMfaStatusResponse {
  smsEnabled: boolean;
  phoneVerified: boolean;
  phoneLastFour: string | null;
}

export interface SmsMfaDisableRequest {
  totpCode: string;
}

export interface SmsMfaDisableResponse {
  smsEnabled: boolean;
  message: string;
}

export interface SmsMfaResendResponse {
  phoneLastFour: string;
  expiresInMinutes: number;
  message: string;
}

export interface SecurityStatus {
  passwordLastChanged: string | null;
  mfaEnabled: boolean;
  mfaMethod: 'totp' | null;
  passkeyEnabled: boolean;
  passkeyCount: number;
  activeSessions: number;
  // SMS MFA fields
  smsEnabled?: boolean;
  phoneVerified?: boolean;
  phoneLastFour?: string | null;
}

// ============================================================================
// Session Preferences Types
// ============================================================================

export type SessionTimeoutOption = '15min' | '1hr' | '4hr' | '24hr' | '7d' | '30d';
export type MfaRememberOption = '1hr' | '24hr' | '7d' | '30d';

export interface SessionPreferences {
  sessionTimeout: SessionTimeoutOption;
  rememberMeEnabled: boolean;
  mfaRememberDuration: MfaRememberOption;
}

export interface UpdateSessionPreferencesRequest {
  sessionTimeout?: SessionTimeoutOption;
  rememberMeEnabled?: boolean;
  mfaRememberDuration?: MfaRememberOption;
}

export interface NotificationPreferences {
  transactionAlerts: boolean;
  weeklyDigest: boolean;
  monthlyReport: boolean;
  budgetAlerts: boolean;
  connectionIssues: boolean;
  securityAlerts: boolean;
  productUpdates: boolean;
  marketingEmails: boolean;
}

export interface PaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface UsageInfo {
  connectedAccounts: number;
  maxAccounts: number;
  historyMonths: number;
  householdMembers?: number;
  maxHouseholdMembers: number;
}

export interface BillingInfo {
  subscription: Subscription;
  paymentMethod: PaymentMethod | null;
  usage: UsageInfo;
}

export interface StripePortalResponse {
  url: string;
}

/**
 * WP-GDPR-3: Delete account request supporting both password and OAuth verification
 */
export interface DeleteAccountRequest {
  /** User's email address for confirmation */
  confirmEmail: string;
  /** Password verification (for users with password) */
  password?: string;
  /** OAuth ID token for re-authentication (for OAuth-only users) */
  oauthIdToken?: string;
  /** OAuth provider that issued the token */
  oauthProvider?: 'google' | 'microsoft' | 'apple';
}

// ============================================================================
// Settings API
// ============================================================================

export const settingsApi = {
  // -------------------------------------------------------------------------
  // Profile
  // -------------------------------------------------------------------------

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<User> => {
    return api.get<User>('/auth/me');
  },

  /**
   * Update current user profile
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    return api.put<User>('/auth/me', data);
  },

  // -------------------------------------------------------------------------
  // Tenant Profile (Household Profile Fields)
  // -------------------------------------------------------------------------

  /**
   * Get tenant profile with decrypted fields
   * @param tenantId - The tenant ID
   */
  getTenantProfile: async (tenantId: string): Promise<TenantProfile> => {
    return api.get<TenantProfile>(`/tenants/${tenantId}/profile`);
  },

  /**
   * Update tenant profile (owner only)
   * @param tenantId - The tenant ID
   * @param data - Profile fields to update (birthYear, stateOfResidence)
   */
  updateTenantProfile: async (
    tenantId: string,
    data: { birthYear?: number | null; stateOfResidence?: string | null }
  ): Promise<TenantProfile> => {
    // Backend accepts camelCase via APISchema
    return api.patch<TenantProfile>(`/tenants/${tenantId}/profile`, data);
  },

  /**
   * Clear birth year from tenant profile (owner only)
   * @param tenantId - The tenant ID
   */
  clearBirthYear: async (tenantId: string): Promise<TenantProfile> => {
    return api.delete<TenantProfile>(`/tenants/${tenantId}/profile/birth-year`);
  },

  // -------------------------------------------------------------------------
  // Security
  // -------------------------------------------------------------------------

  /**
   * Get security status (MFA, passkey, sessions)
   * Fetches from /auth/mfa endpoint and transforms to SecurityStatus type
   */
  getSecurityStatus: async (): Promise<SecurityStatus> => {
    // Backend returns camelCase via APISchema
    interface MfaStatusBackend {
      mfaEnabled: boolean;
      totpEnabled: boolean;
      smsEnabled: boolean;
      hasRecoveryCodes: boolean;
      phoneVerified: boolean;
      phoneLastFour: string | null;
      passkeyEnabled: boolean;
      passkeyCount: number;
    }

    const backendStatus = await api.get<MfaStatusBackend>('/auth/mfa');

    // Map backend response to SecurityStatus
    return {
      passwordLastChanged: null, // Not available from MFA endpoint
      mfaEnabled: backendStatus.mfaEnabled,
      mfaMethod: backendStatus.totpEnabled ? 'totp' : null,
      passkeyEnabled: backendStatus.passkeyEnabled,
      passkeyCount: backendStatus.passkeyCount,
      activeSessions: 1, // Not available from MFA endpoint, default to 1 (current session)
      smsEnabled: backendStatus.smsEnabled,
      phoneVerified: backendStatus.phoneVerified,
      phoneLastFour: backendStatus.phoneLastFour,
    };
  },

  /**
   * Change password
   * This will invalidate all other sessions
   */
  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    return api.post('/auth/change-password', data);
  },

  /**
   * Start MFA setup - returns secret and QR code
   */
  setupMfa: async (): Promise<MfaSetupResponse> => {
    return api.post<MfaSetupResponse>('/auth/mfa/totp/setup');
  },

  /**
   * Verify MFA setup with TOTP code
   * Returns recovery codes on success
   */
  verifyMfaSetup: async (data: MfaVerifyRequest): Promise<MfaVerifyResponse> => {
    return api.post<MfaVerifyResponse>('/auth/mfa/totp/verify', data);
  },

  /**
   * Disable MFA
   * Requires password confirmation
   */
  disableMfa: async (data: MfaDisableRequest): Promise<void> => {
    return api.post('/auth/mfa/totp/disable', data);
  },

  // -------------------------------------------------------------------------
  // SMS MFA
  // -------------------------------------------------------------------------

  /**
   * Get SMS MFA status
   */
  getSmsMfaStatus: async (): Promise<SmsMfaStatusResponse> => {
    return api.get<SmsMfaStatusResponse>('/auth/mfa/sms/status');
  },

  /**
   * Initiate SMS MFA setup
   * Requires TOTP to be enabled first
   * Sends verification code to phone number
   */
  setupSmsMfa: async (data: SmsMfaSetupRequest): Promise<SmsMfaSetupResponse> => {
    // Backend accepts camelCase via APISchema
    return api.post<SmsMfaSetupResponse>('/auth/mfa/sms/setup', data);
  },

  /**
   * Verify SMS code to complete setup
   */
  verifySmsMfaSetup: async (data: SmsMfaVerifyRequest): Promise<SmsMfaVerifyResponse> => {
    return api.post<SmsMfaVerifyResponse>('/auth/mfa/sms/verify', data);
  },

  /**
   * Disable SMS MFA
   * Requires TOTP code verification (not SMS code - SIM swap protection)
   */
  disableSmsMfa: async (data: SmsMfaDisableRequest): Promise<SmsMfaDisableResponse> => {
    // Backend accepts camelCase via APISchema
    return api.post<SmsMfaDisableResponse>('/auth/mfa/sms/disable', data);
  },

  /**
   * Resend SMS verification code during setup
   * Rate limited
   */
  resendSmsMfaCode: async (): Promise<SmsMfaResendResponse> => {
    return api.post<SmsMfaResendResponse>('/auth/mfa/sms/resend');
  },

  /**
   * Log out of all sessions
   * This will also log out the current session
   */
  logoutAllSessions: async (): Promise<void> => {
    return api.post('/auth/logout-all');
  },

  // -------------------------------------------------------------------------
  // Session Preferences
  // -------------------------------------------------------------------------

  /**
   * Get session preferences (timeout, remember me, MFA remember duration)
   */
  getSessionPreferences: async (): Promise<SessionPreferences> => {
    return api.get<SessionPreferences>('/auth/session-preferences');
  },

  /**
   * Update session preferences
   * Only provided fields will be updated
   */
  updateSessionPreferences: async (
    data: UpdateSessionPreferencesRequest
  ): Promise<SessionPreferences> => {
    return api.put<SessionPreferences>('/auth/session-preferences', data);
  },

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------

  /**
   * Get notification preferences
   */
  getNotificationPreferences: async (): Promise<NotificationPreferences> => {
    return api.get<NotificationPreferences>('/users/me/notifications');
  },

  /**
   * Update notification preferences
   */
  updateNotificationPreferences: async (
    data: NotificationPreferences
  ): Promise<NotificationPreferences> => {
    return api.put<NotificationPreferences>('/users/me/notifications', data);
  },

  // -------------------------------------------------------------------------
  // Billing
  // -------------------------------------------------------------------------

  /**
   * Get billing information including subscription, payment method, and usage
   */
  getBillingInfo: async (): Promise<BillingInfo> => {
    return api.get<BillingInfo>('/billing/info');
  },

  /**
   * Get Stripe customer portal URL for managing subscription
   */
  getStripePortalUrl: async (): Promise<StripePortalResponse> => {
    return api.post<StripePortalResponse>('/billing/portal');
  },

  /**
   * Get checkout URL for upgrading to a new plan
   */
  getUpgradeUrl: async (tier: SubscriptionTier): Promise<StripePortalResponse> => {
    return api.post<StripePortalResponse>('/billing/upgrade', { tier });
  },

  // -------------------------------------------------------------------------
  // Account Deletion
  // -------------------------------------------------------------------------

  /**
   * Permanently delete account (GDPR Article 17 - Right to Erasure)
   * This action cannot be undone. All personal data will be anonymized.
   *
   * @param data.confirmEmail - Must match account email
   * @param data.password - Current password for verification
   */
  deleteAccount: async (data: DeleteAccountRequest): Promise<void> => {
    return api.delete('/users/me', data);
  },
};

export default settingsApi;
